import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import assessmentPackWaveBg from "../../../assets/assessment-pack-wave-bg.png";
import type { AssessmentPack } from "../../../services/assessmentPackService";
import { assessmentPackWindowName } from "./assessmentPackWindow";

const chipSx = {
  bgcolor: "#F1F2F4",
  color: "#404040",
  borderRadius: "10px",
  px: 1.5,
  height: 22,
  display: "inline-flex",
  alignItems: "center",
  fontSize: 12,
  lineHeight: 1,
} as const;

function ChipRow({ labels }: { labels: string[] }) {
  if (labels.length === 0) {
    return (
      <Typography sx={{ fontSize: 12, color: "#6A7282" }}>–</Typography>
    );
  }

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
      {labels.map((label, index) => (
        <Box component="span" key={`${label}-${index}`} sx={chipSx}>
          {label}
        </Box>
      ))}
    </Box>
  );
}

export const AssessmentPackCard: React.FC<{ pack: AssessmentPack }> = ({
  pack,
}) => {
  return (
    <Box
      sx={{
        width: "100%",
        boxSizing: "border-box",
        border: "1px solid #E5E7EB",
        borderRadius: "16px",
        bgcolor: "#fff",
        p: 2.5,
        display: "flex",
        flexDirection: "column",
        boxShadow: "0 1px 2px rgba(25,37,50,0.10)",
        "&:hover .assessment-pack-view-details, &:focus-within .assessment-pack-view-details":
          {
            opacity: 1,
            transform: "translateY(0)",
            pointerEvents: "auto",
          },
      }}
    >
      <Box
        sx={{
          position: "relative",
          height: 134,
          borderRadius: "16px",
          overflow: "hidden",
          flex: "none",
          backgroundColor: "#E7ECFC",
          backgroundImage: `url(${assessmentPackWaveBg})`,
          backgroundPosition: "center",
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(to top, rgba(238,243,254,0.92) 0%, rgba(238,243,254,0.4) 44%, rgba(238,243,254,0) 74%)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            left: 20,
            right: 20,
            bottom: 14,
          }}
        >
          <Typography sx={{ fontSize: 13, color: "#6A7282", mb: 0.75 }}>
            Assessment Pack
          </Typography>
          <Typography
            title={pack.name}
            sx={{
              fontSize: 18,
              fontWeight: 700,
              color: "#0D0D0D",
              lineHeight: "25px",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {pack.name}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mt: 3, mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.25 }}>
          <CategoryOutlinedIcon sx={{ fontSize: 16, color: "#A6A6A6" }} aria-hidden />
          <Typography sx={{ fontSize: 12, color: "#6A7282" }}>
            Impact Items
          </Typography>
        </Box>
        <ChipRow labels={pack.impactItems} />
      </Box>

      <Box
        sx={{
          borderTop: "1px dashed #D9D9D9",
          mt: "auto",
          mb: 2,
        }}
      />

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          minHeight: 34,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            component="span"
            sx={{
              bgcolor: "#E7ECFC",
              color: "#295EF1",
              borderRadius: "10px",
              px: 1,
              height: 20,
              maxWidth: 160,
              display: "flex",
              alignItems: "center",
              fontSize: 12,
              fontWeight: 600,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            1
          </Box>
          <Typography sx={{ fontSize: 13, color: "#404040" }}>
            Ref. Object
          </Typography>
        </Box>
        {pack.detailUrl ? (
          <Button
            className="assessment-pack-view-details"
            component="a"
            href={pack.detailUrl}
            target={assessmentPackWindowName(pack.detailUrl)}
            rel="noopener noreferrer"
            variant="contained"
            size="small"
            aria-label={`View Details, ${pack.name}`}
            sx={{
              "&&": {
                width: 114,
                minWidth: 114,
                height: 34,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxSizing: "border-box",
                padding: 0,
                backgroundColor: "#295EF1",
                color: "#fff",
                borderRadius: "3px",
                fontSize: 14,
                fontWeight: 500,
                lineHeight: "34px",
                textDecoration: "none",
                textTransform: "none",
                boxShadow: "none",
                opacity: 0,
                transform: "translateY(4px)",
                pointerEvents: "none",
                transition: "opacity 160ms, transform 160ms",
              },
              "&&:hover, &&:active, &&.Mui-focusVisible": {
                backgroundColor: "#295EF1",
                color: "#fff",
                fontWeight: 500,
                boxShadow: "none",
              },
            }}
          >
            View Details
          </Button>
        ) : null}
      </Box>
    </Box>
  );
};
